<template>
  <div
    class="blok-rte"
    :class="blok?.Narrow_text ? 'narrow-text' : 'wide-text'"
  >
    <template v-for="(item, idx) in contentItems" :key="idx">
      <BlokTable v-if="item.type === 'table'" :blok="{ content: item }" :alternate="alternate" />
      <div v-else v-html="item.html"></div>
    </template>
  </div>
</template>

<script setup>
const props = defineProps({
  blok: {
    type: Object,
    required: true,
  },
  alternate: {
    type: Boolean,
    default: false,
  }
});

const { blok } = toRefs(props);

const contentItems = computed(() => {
  const content = blok.value.content;
  const hasTableInContent = content?.content?.some(item => item.type === 'table');

  if (!hasTableInContent) {
    const result = useRenderRichText(content);
    const html = result.replace(/<li>\s*<p>(.*?)<\/p>\s*<\/li>/g, "<li>$1</li>");
    return [{ type: 'html', html }];
  }

  const items = [];
  let textBuffer = [];

  content.content.forEach((item) => {
    if (item.type === 'table') {
      if (textBuffer.length > 0) {
        const result = useRenderRichText({ type: 'doc', content: textBuffer });
        items.push({ type: 'html', html: result.replace(/<li>\s*<p>(.*?)<\/p>\s*<\/li>/g, "<li>$1</li>") });
        textBuffer = [];
      }
      items.push({ type: 'table', ...item });
    } else {
      textBuffer.push(item);
    }
  });

  if (textBuffer.length > 0) {
    const result = useRenderRichText({ type: 'doc', content: textBuffer });
    items.push({ type: 'html', html: result.replace(/<li>\s*<p>(.*?)<\/p>\s*<\/li>/g, "<li>$1</li>") });
  }

  return items;
});
</script>

<style lang="scss" scoped>
// Base design
.blok-rte {
  &:has(.blok-table) {
    width: 100vw;
    margin-left: calc(-50vw + 50%);
    padding: 4rem calc(50vw - 50%);
  }

  :deep(ul) {
    list-style: disc;
    margin-left: 1rem;
    padding-left: 0.125rem;
  }

  :deep(h2) {
    @include size-xl;
    span {
      @include size-xl;
    }
  }

  > div:not(.blok-table) :deep(h2) { 
    margin: 2rem 0 
  }

  > div:not(.blok-table) :deep(p) { 
    margin-top: 2rem;
  }


  > div:first-child:has(+ .blok-table) {
    :deep(h2) {
      font-size: 48px;
      margin-bottom: 1rem;

      @include for-phone-only {
        font-size: 28px;
      }
    }

    :deep(p) {
      font-size: 18px;

      @include for-phone-only {
        font-size: 16px;
      }
    }
  }

  .blok-table {
    :deep(tbody tr:first-child p) {
      font-size: 18px !important;
    }

    :deep(tbody tr:not(:first-child) p) {
      font-size: 16px !important;
    }
  }

  :deep(h3) {
    @include size-l;
    span {
      @include size-l;
    }
  }

  :deep(h4) {
    @include size-m;
    span {
      @include size-m;
    }
  }

  :deep(p) {
    @include size-s;
  }

  :deep(ul p) {
    @include size-xs;
  }

  :deep(a) {
    color: var(--text-100);
  }

  > :deep(*:not(:first-child)) {
    margin-top: 2rem;
  }

  > div:not(:first-child) {
    margin-top: 2rem;
  }

  > div:has(+ .blok-table) + .blok-table {
    margin-top: 80px !important;
  }

  > .blok-table:not(:first-child):not(:nth-child(2)) {
    margin-top: 2rem !important;
  }

  &.section {
    margin-bottom: 0;
    display: block;
    margin: 0 auto;
    &.narrow-text {
      width: 51%;
    }

    &.wide-text {
      width: 70%;
    }
  }
}
</style>
